import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { login } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { getDashboard } from '../services/userService';
import { hasActiveSubscriptionStatus } from '../utils/subscription';

const Login: React.FC = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const navigate = useNavigate();
  const { loginUser, setSubscriptionStatus } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await login({ email: trimmedEmail, password });
      if (response.data?.token) {
        loginUser(response.data.token, response.data.user);
        if (response.data.user.is_admin) {
          setSubscriptionStatus(false);
          navigate('/live-classes/');
          return;
        }

        try {
          const dashboardResponse = await getDashboard(response.data.token);
          const hasActiveSubscription = hasActiveSubscriptionStatus(dashboardResponse.data.subscriptions);
          setSubscriptionStatus(hasActiveSubscription);
          navigate(hasActiveSubscription ? '/account/' : '/pricing/');
        } catch {
          setSubscriptionStatus(false);
          navigate('/pricing/');
        }
      } else {
        setError(response.message || 'Login failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Header />
    <main className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden font-sans">
      
      {/* Background Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none" 
        style={{ 
          backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)', 
          backgroundSize: '40px 40px' 
        }}
      ></div>
      
      {/* Ambient Glow */}
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#f59e0b]/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-lg px-6">
        <div className="bg-white border border-zinc-100 rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.05)] p-8 md:p-10">
          
          {/* Logo / Icon Area */}
          <div className="flex flex-col items-center mb-8">
          
            <h2 className="text-3xl font-black text-zinc-950 tracking-tighter">Welcome Back</h2>
            <p className="text-zinc-500 text-sm font-medium mt-1">Access your scaling dashboard</p>
          </div>

          {error && (
            <div className="mb-2 px-4 py-3 rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* Email Input */}
            <div className="relative group">
              <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1 block">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-amber-600 transition-colors" />
                <input
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-4 focus:ring-amber-400/5 transition-all"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="relative group">
              <div className="flex justify-between items-center mb-2 ml-1">
                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                  Password <span className="text-red-500">*</span>
                </label>
                <Link to="/forgot-password/" className="hidden text-[10px] font-bold hover:text-zinc-950 transition-colors hover:underline">
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-amber-600 transition-colors" />
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-4 focus:ring-amber-400/5 transition-all"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#09090B] text-white border border-zinc-300 px-8 py-4 rounded-xl font-bold text-lg text-center hover:bg-white hover:text-zinc-950 hover:border-[#09090B] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing In...' : (<>Sign In <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          {/* Registration Footer */}
          <div className="mt-8 text-center border-t border-zinc-50 pt-8">
            <p className="text-zinc-400 text-sm font-medium">
              Don't have an account?{' '}
              <Link to="/register/" className="text-zinc-950 font-black hover:underline underline-offset-4 transition-colors">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
    <Footer />
    </>
  );
};

export default Login;
